import * as React from 'react';
import { Empty, List, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getOrgLoading,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { Organisation } from 'store/organisation/models';
import { StyledCard } from 'components/Custom/card';
import { CardTitleLargeSpan } from 'components/Custom/spans';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';
import { TransactionEditDrawerLinesScroll } from './transactions-tab/styles';
import styled from 'styled-components';
import { RoundInput } from 'components/Custom/inputs';
import { SearchOutlined } from '@ant-design/icons';

const { Text } = Typography;

/** Space reserved above the scrollable list (tabs, card header, padding). */
const TRANSACTION_ITEMS_LIST_VIEWPORT_OFFSET_WIDE = 200;
const TRANSACTION_ITEMS_LIST_VIEWPORT_OFFSET_NARROW = 240;
const TRANSACTION_ITEMS_LIST_MIN_HEIGHT = 320;

const TransactionItemOptions = styled.div`
  width: 100%;
  max-width: 100%;
  margin-top: 12px;

  .ant-list {
    width: 100%;
  }

  .ant-list-items {
    display: grid;
    gap: 8px;
  }

  .ant-list-item {
    padding: 10px 12px;
    margin: 0;
    border-radius: 10px;
    border: 1px solid rgba(4, 42, 42, 0.06);
    background: rgba(255, 255, 255, 0.9);
    transition: background 120ms ease, border-color 120ms ease;
  }

  .ant-list-item:hover {
    background: var(--color-surface-raised, #ffffff);
    border-color: rgba(4, 42, 42, 0.12);
  }
`;

const ENTITY_DETAILS_BREAKPOINT = 1500;
const ENTITY_DETAILS_HEIGHT_BREAKPOINT = 750;

const toTitleCase = (value?: string) => {
  if (!value) return '-';
  return value
    .trim()
    .split(/\s+/)
    .map((word) =>
      word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : ''
    )
    .join(' ');
};

/** Narrow layout: match chart-of-accounts / tax-rates reference tabs. */
const TC_NARROW_MAIN_COLUMN_STYLE: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  marginRight: '24px',
  boxSizing: 'border-box',
  paddingRight: 'clamp(72px, 16vw, 40px)',
};

const TC_NARROW_INNER_STYLE: React.CSSProperties = {
  padding: '0 40px',
  width: '100%',
  maxWidth: 1250,
  margin: '0 auto',
  boxSizing: 'border-box',
  minWidth: 0,
};

type TransactionItemRow = {
  id: string;
  name: string;
};

interface TransactionItemsTabProps {
  organisation?: Organisation;
  organisationId?: string;
}

export const TransactionItemsTab: React.FC<TransactionItemsTabProps> = ({
  organisation,
  organisationId: organisationIdProp,
}) => {
  const { t } = useTranslation();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const tcWideLayout =
    windowWidth > ENTITY_DETAILS_BREAKPOINT &&
    windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;

  const organisationId = String(
    organisationIdProp ?? organisation?.uuid ?? ''
  ).trim();
  const orgLoading = useSelector(getOrgLoading);
  const organisations = useSelector(getOrgOrganisations);

  const storeOrganisation = React.useMemo(
    () => organisations?.find((o) => o.uuid === organisationId),
    [organisations, organisationId]
  );

  const resolvedOrganisation = organisation ?? storeOrganisation;
  const transactionItems = React.useMemo(
    () => resolvedOrganisation?.transactionItems ?? [],
    [resolvedOrganisation?.transactionItems]
  );

  const itemsLoaded = resolvedOrganisation?.transactionItems !== undefined;
  const tcLoading = !!organisationId && (!itemsLoaded || orgLoading);

  const dataSource = React.useMemo(
    () =>
      transactionItems
        .map((item) => {
          const id = (item.id ?? '').trim();
          if (!id) return null;
          const name = (item.name ?? '').trim();
          return { id, name: name || id };
        })
        .filter((row): row is TransactionItemRow => row != null)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        ),
    [transactionItems]
  );

  const [searchText, setSearchText] = React.useState('');
  const filteredDataSource = React.useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return dataSource;
    return dataSource.filter(
      (item) =>
        item.name.toLowerCase().includes(q) || item.id.toLowerCase().includes(q)
    );
  }, [dataSource, searchText]);

  const listMaxHeight = React.useMemo(
    () =>
      Math.max(
        TRANSACTION_ITEMS_LIST_MIN_HEIGHT,
        windowHeight -
          (tcWideLayout
            ? TRANSACTION_ITEMS_LIST_VIEWPORT_OFFSET_WIDE
            : TRANSACTION_ITEMS_LIST_VIEWPORT_OFFSET_NARROW)
      ),
    [windowHeight, tcWideLayout]
  );

  const tcTableCard = (
    <StyledCard
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: tcWideLayout ? 600 : 0,
        ...(!tcWideLayout ? { overflowX: 'auto' as const } : {}),
      }}
    >
      <div
        style={{
          padding: '5px',
          paddingLeft: '5px',
          paddingTop: '5px',
          minWidth: tcWideLayout ? 500 : 0,
          margin: 'auto',
        }}
      >

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 40,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minHeight: '32px',
            }}
          >
            <CardTitleLargeSpan style={{ fontSize: 20, lineHeight: '32px' }}>
              {t('organisation.transaction-items')}
            </CardTitleLargeSpan>
            <RoundInput
              prefix={
                <SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              }
              placeholder={t('organisation.search-items')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              width="260px"
              height="32px"
              style={{ fontSize: 13 }}
            />

          </div>
        </div>



        <Spin spinning={tcLoading}>
          {filteredDataSource.length > 0 ? (
            <TransactionItemOptions>
              <TransactionEditDrawerLinesScroll style={{ maxHeight: listMaxHeight }}>
                <List
                  size="small"
                  dataSource={filteredDataSource}
                  renderItem={(item) => (
                    <List.Item key={item.id}>
                      <Text style={{ color: 'var(--color-text-dark, #042a2a)' }}>
                        {toTitleCase(item.name)}
                      </Text>
                    </List.Item>
                  )}
                />
              </TransactionEditDrawerLinesScroll>
            </TransactionItemOptions>
          ) : !tcLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text style={{ color: 'var(--color-text-muted, #5a6a6a)' }}>
                    {dataSource.length > 0 && searchText.trim()
                      ? t('organisation.no-entities-found-title', 'No results found')
                      : t('global.no-data-available', 'No data available')}
                  </Text>
                }
              />
            </div>
          ) : null}
        </Spin>
      </div>
    </StyledCard>
  );

  return (
    <div
      className="entity-tracking-categories-tab"
      style={{ minHeight: 'calc(100vh - 120px)' }}
    >
      <div
        className={`tracking-categories-tab-wrapper${!tcWideLayout ? ' tracking-categories-tab-wrapper--narrow' : ''
          }`}
      >
        {tcWideLayout ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 20,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                flex: '1 1 auto',
                minWidth: 600,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              {tcTableCard}
            </div>
            <div
              style={{
                width: TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
                flexShrink: 0,
              }}
            />
          </div>
        ) : (
          <div className="entity-overview-tab entity-details-narrow-side-shell">
            <div style={TC_NARROW_MAIN_COLUMN_STYLE}>
              <div style={TC_NARROW_INNER_STYLE}>{tcTableCard}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
